import { useCallback, useEffect, useMemo, useState } from "react";
import {
  queueAPI,
  scheduleAPI,
  stageAssignmentsAPI,
  tenantAPI,
} from "../../api/client";
import {
  todayString,
  toLocalTimeDisplay,
  toLocalTimeString,
} from "../../utils/dateUtils";
import { formatAssignmentStatus } from "../../utils/formatAssignmentStatus";
import ConflictWarning from "./ConflictWarning";
import EligibleUserSelect from "./EligibleUserSelect";
import EndDateBreakdown from "./EndDateBreakdown";

export default function StageAssignTab({
  project,
  stageDef,
  selectedStageId,
  activeAssignments,
  maxConcurrent,
  onAssignStage,
  onReassign,
  onReschedule,
  onRemove,
  loading,
}) {
  const [stageUserId, setStageUserId] = useState("");
  const [startDate, setStartDate] = useState(() => todayString());
  const [endDateResult, setEndDateResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [conflictDismissed, setConflictDismissed] = useState(false);
  const [userScheduleSummary, setUserScheduleSummary] = useState(null);
  const [activeShiftName, setActiveShiftName] = useState(null);
  const [queuePosition, setQueuePosition] = useState(null);
  const [reassignTargets, setReassignTargets] = useState({});
  const [stageEligibleUsers, setStageEligibleUsers] = useState([]);
  const [scheduledStartTime, setScheduledStartTime] = useState("09:00");
  // Written at the moment of allocation and sent with it, so the assignee gets
  // their brief in the same action rather than a second visit to the
  // Work Instructions tab. It shows up there afterwards like any other.
  const [primaryInstruction, setPrimaryInstruction] = useState("");
  const [startTimeSuggestion, setStartTimeSuggestion] = useState(null);
  const [overflowOption, setOverflowOption] = useState(null);
  const [overflowData, setOverflowData] = useState(null);
  const [nextDayDate, setNextDayDate] = useState(null);
  const [reschedulingId, setReschedulingId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleIsOvertime, setRescheduleIsOvertime] = useState(false);
  const [rescheduleEndResult, setRescheduleEndResult] = useState(null);
  const [rescheduleCalculating, setRescheduleCalculating] = useState(false);
  const [rescheduleSaving, setRescheduleSaving] = useState(false);

  const durationValue = useMemo(() => {
    const value = Number(stageDef?.estimatedDuration || 0);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }, [stageDef]);

  const durationUnit = useMemo(() => {
    const unit = String(stageDef?.durationUnit || "DAYS").toUpperCase();
    return ["MINUTES", "HOURS", "DAYS"].includes(unit) ? unit : "DAYS";
  }, [stageDef]);

  const canAssignMoreLocal = activeAssignments.length < maxConcurrent;

  const selectedAlreadyAssigned = activeAssignments.some(
    (assignment) => assignment.assignee?.id === stageUserId,
  );
  const existingAssignment = activeAssignments[0] || null;
  const assignedUserIds = useMemo(
    () =>
      new Set(activeAssignments.map((assignment) => assignment.assignee?.id)),
    [activeAssignments],
  );

  const formatDateInput = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-CA");
  };

  const formatTimeInput = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      const timeStr = String(value);
      const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
      if (!match) return "";
      return `${String(match[1]).padStart(2, "0")}:${match[2]}`;
    }
    return `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes(),
    ).padStart(2, "0")}`;
  };

  const selectedUser = useMemo(() => {
    const candidates = [
      ...(project?.allocatedUsers || []),
      ...stageEligibleUsers,
    ];
    return candidates.find((user) => user.id === stageUserId) || null;
  }, [project?.allocatedUsers, stageEligibleUsers, stageUserId]);

  // Derive preview params based on selected overflow option
  const previewParams = useMemo(() => {
    if (!overflowData) {
      return { startDate, scheduledStartTime, isOvertime: false };
    }
    if (overflowOption === "OVERTIME") {
      return { startDate, scheduledStartTime, isOvertime: true };
    }
    if (overflowOption === "NEXT_DAY") {
      return {
        startDate:
          nextDayDate ||
          (() => {
            const d = new Date(startDate);
            d.setDate(d.getDate() + 1);
            return d.toLocaleDateString("en-CA");
          })(),
        scheduledStartTime: startTimeSuggestion?.workStartTime || "09:00",
        isOvertime: false,
      };
    }
    // SPLIT
    return { startDate, scheduledStartTime, isOvertime: false };
  }, [
    overflowOption,
    overflowData,
    startDate,
    scheduledStartTime,
    startTimeSuggestion,
    nextDayDate, // ← stable state, not endDateResult
  ]);

  useEffect(() => {
    setStageUserId("");
    setPrimaryInstruction("");
    setStartDate(todayString());
    setEndDateResult(null);
    setConflictData(null);
    setConflictDismissed(false);
    setReassignTargets({});
    setScheduledStartTime("09:00");
    setStartTimeSuggestion(null);
    setOverflowOption(null);
    setOverflowData(null);
    setNextDayDate(null);
    setReschedulingId(null);
    setRescheduleDate("");
    setRescheduleTime("");
    setRescheduleIsOvertime(false);
    setRescheduleEndResult(null);
    setRescheduleCalculating(false);
    setRescheduleSaving(false);
    setUserScheduleSummary(null);
    setActiveShiftName(null);
  }, [selectedStageId]);

  useEffect(() => {
    if (!selectedStageId || !project?.id) return;
    stageAssignmentsAPI
      .eligibleUsersForReassign(project.id, selectedStageId)
      .then((res) => setStageEligibleUsers(res.data || []))
      .catch(() => {});
  }, [selectedStageId, project?.id]);

  useEffect(() => {
    if (!stageUserId) {
      setUserScheduleSummary(null);
      setActiveShiftName(null);
      setQueuePosition(null);
      return;
    }
    Promise.all([
      scheduleAPI.getUser(stageUserId),
      queueAPI.getUserQueue(stageUserId),
      tenantAPI.getTenant(),
    ])
      .then(([schedRes, queueRes, tenantRes]) => {
        const activeSchedule = schedRes.data?.activeSchedule || null;
        setUserScheduleSummary(activeSchedule);
        setQueuePosition((queueRes.data || []).length + 1);

        const shifts = Array.isArray(tenantRes.data?.projectFormSchema?.shifts)
          ? tenantRes.data.projectFormSchema.shifts
          : [];
        if (activeSchedule && shifts.length > 0) {
          const sortDays = (days) => [...(days || [])].sort().join(",");
          const matched = shifts.find(
            (s) =>
              s.workStartTime === activeSchedule.workStartTime &&
              s.workEndTime === activeSchedule.workEndTime &&
              sortDays(s.workingDays) === sortDays(activeSchedule.workingDays),
          );
          setActiveShiftName(matched?.name || null);
        } else {
          setActiveShiftName(null);
        }
      })
      .catch(() => {});
  }, [stageUserId]);

  useEffect(() => {
    if (!stageUserId || !existingAssignment) return;
    const nextStartDate = formatDateInput(
      existingAssignment.startDate || existingAssignment.assignedAt,
    );
    const nextStartTime = formatTimeInput(
      existingAssignment.scheduledStartTime ||
        existingAssignment.assignedAt ||
        "",
    );
    if (nextStartDate) setStartDate(nextStartDate);
    if (nextStartTime) setScheduledStartTime(nextStartTime);
  }, [
    stageUserId,
    existingAssignment?.startDate,
    existingAssignment?.scheduledStartTime,
    existingAssignment?.assignedAt,
  ]);

  useEffect(() => {
    if (!stageUserId || !startDate) return;
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const res = await scheduleAPI.suggestStartTime(stageUserId, {
          date: startDate,
          projectId: project.id,
          stageId: selectedStageId,
        });
        if (active) {
          setStartTimeSuggestion(res.data);
          if (
            res.data?.suggestedStartTime &&
            !(existingAssignment && activeAssignments.length > 0)
          ) {
            setScheduledStartTime(res.data.suggestedStartTime);
          }
        }
      } catch {
        if (active) setStartTimeSuggestion(null);
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [stageUserId, startDate, existingAssignment, activeAssignments.length]);

  useEffect(() => {
    if (!stageUserId || !previewParams.startDate) {
      setEndDateResult(null);
      return;
    }

    // For OVERTIME — skip the working hours calculation entirely
    // just show start → start + duration
    // In the calculateEndDate useEffect, overtime branch:
    if (previewParams.isOvertime) {
      const resolvedHoursPerDay = Number(startTimeSuggestion?.hoursPerDay);
      if (!Number.isFinite(resolvedHoursPerDay) || resolvedHoursPerDay <= 0) {
        setEndDateResult(null);
        setIsCalculating(false);
        return;
      }

      const [h, m] = previewParams.scheduledStartTime.split(":").map(Number);
      const startMins = h * 60 + m;
      const overtimeDurationMins =
        durationUnit === "MINUTES"
          ? durationValue
          : durationUnit === "HOURS"
            ? durationValue * 60
            : durationValue * resolvedHoursPerDay * 60;
      const endMins = startMins + overtimeDurationMins;
      const endH = Math.floor(endMins / 60);
      const endM = endMins % 60;
      const endTimeStr = `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;

      const [startDateYear, startDateMonth, startDateDay] =
        previewParams.startDate.split("-").map(Number);
      const endDateTime = new Date(
        startDateYear,
        startDateMonth - 1,
        startDateDay,
        endH,
        endM,
        0,
        0,
      );

      setEndDateResult({
        endDate: previewParams.startDate,
        expectedEndDate: endDateTime.toISOString(),
        isOvertime: true,
        overtimeEndTime: endTimeStr,
        workStartTime: previewParams.scheduledStartTime,
        workEndTime: endTimeStr,
        hoursPerDay: resolvedHoursPerDay,
        workingDays: startTimeSuggestion?.workingDays || [],
        summary: {
          durationHours:
            durationUnit === "MINUTES"
              ? durationValue / 60
              : durationUnit === "HOURS"
                ? durationValue
                : durationValue * resolvedHoursPerDay,
          workingDaysCount: 1,
          weeklyOffs: 0,
          holidays: 0,
          totalCalendarDays: 1,
          hoursPerDay: resolvedHoursPerDay,
          startedPartialDay: true,
          hoursRemainingOnStartDay: resolvedHoursPerDay,
        },
        collapsedBreakdown: [
          {
            date: previewParams.startDate,
            dayName: new Date(
              startDateYear,
              startDateMonth - 1,
              startDateDay,
            ).toLocaleDateString("en-IN", { weekday: "short" }),
            type: "WORKING",
            isStartDate: true,
            isEndDate: true,
            isPartialDay: true,
            hoursUsed:
              durationUnit === "MINUTES"
                ? durationValue / 60
                : durationUnit === "HOURS"
                  ? durationValue
                  : durationValue * resolvedHoursPerDay,
            label: `${
              durationUnit === "MINUTES"
                ? durationValue / 60
                : durationUnit === "HOURS"
                  ? durationValue
                  : durationValue * resolvedHoursPerDay
            }h of ${resolvedHoursPerDay}h`,
          },
        ],
      });
      setIsCalculating(false);
      return;
    }

    setEndDateResult(null);
    let active = true;
    const timer = setTimeout(async () => {
      setIsCalculating(true);
      try {
        const res = await scheduleAPI.calculateEndDate(stageUserId, {
          startDate: previewParams.startDate,
          scheduledStartTime: previewParams.scheduledStartTime,
          durationValue,
          durationUnit,
        });
        if (active) setEndDateResult(res.data);
      } catch {
        if (active) setEndDateResult(null);
      } finally {
        if (active) setIsCalculating(false);
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [stageUserId, previewParams, durationValue, durationUnit]);

  useEffect(() => {
    if (overflowOption !== "NEXT_DAY") {
      setNextDayDate(null);
      return;
    }
    // Use the breakdown from the last non-NEXT_DAY result to find next working day
    // If not available, just add 1 day as fallback
    const nextWorkingDay = endDateResult?.breakdown?.find(
      (day) => day.type === "WORKING" && day.date > startDate,
    );
    if (nextWorkingDay) {
      setNextDayDate(nextWorkingDay.date);
    } else {
      const d = new Date(startDate);
      d.setDate(d.getDate() + 1);
      setNextDayDate(d.toLocaleDateString("en-CA"));
    }
    // Intentionally NOT including endDateResult in deps — only run when option changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overflowOption, startDate]);

  // Reset nextDayDate when startDate or overflow clears
  useEffect(() => {
    if (!overflowData) setNextDayDate(null);
  }, [overflowData]);

  useEffect(() => {
    if (!selectedStageId || !startDate || !project?.id) {
      setConflictData(null);
      return;
    }
    setConflictDismissed(false);
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const res = await stageAssignmentsAPI.checkConflicts(project.id, {
          stageId: selectedStageId,
          startDate,
        });
        if (active) setConflictData(res.data);
      } catch {
        if (active) setConflictData({ hasConflict: false, conflicts: [] });
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [startDate, selectedStageId, project?.id]);

  const showConflictWarning = conflictData?.hasConflict && !conflictDismissed;

  const isStartDateWO = useMemo(() => {
    if (!startDate || !startTimeSuggestion?.workingDays) return false;
    const WEEK_KEYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const [y, m, d] = startDate.split("-").map(Number);
    const dayKey = WEEK_KEYS[new Date(y, m - 1, d).getDay()];
    return !startTimeSuggestion.workingDays.includes(dayKey);
  }, [startDate, startTimeSuggestion?.workingDays]);

  useEffect(() => {
    if (!startTimeSuggestion || !scheduledStartTime) return;

    const parseToMinutes = (time) => {
      const [h, m] = String(time || "00:00")
        .split(":")
        .map(Number);
      return h * 60 + (m || 0);
    };

    const startMins = parseToMinutes(scheduledStartTime);
    const durationMins =
      durationUnit === "MINUTES"
        ? durationValue
        : durationUnit === "HOURS"
          ? durationValue * 60
          : durationValue * (startTimeSuggestion.hoursPerDay || 8) * 60;
    const endMins = startMins + durationMins;
    const workEndMins = parseToMinutes(startTimeSuggestion.workEndTime);

    if (endMins > workEndMins) {
      const overtimeMins = endMins - workEndMins;
      const remainingToday = workEndMins - startMins;

      setOverflowData({
        overtimeMinutes: overtimeMins,
        remainingTodayMinutes: remainingToday,
        endTime: `${String(Math.floor(endMins / 60)).padStart(2, "0")}:${String(
          endMins % 60,
        ).padStart(2, "0")}`,
        workEndTime: startTimeSuggestion.workEndTime,
      });
      setOverflowOption("SPLIT");
    } else {
      setOverflowData(null);
      setOverflowOption(null);
    }
  }, [scheduledStartTime, durationValue, durationUnit, startTimeSuggestion]);

  const buildOvertimePreviewResult = useCallback(
    (startDateValue, startTimeValue, scheduleMeta) => {
      if (!startDateValue || !startTimeValue) return null;
      const [year, month, day] = startDateValue.split("-").map(Number);
      const [hours, minutes] = startTimeValue.split(":").map(Number);
      if (
        !Number.isFinite(year) ||
        !Number.isFinite(month) ||
        !Number.isFinite(day) ||
        !Number.isFinite(hours) ||
        !Number.isFinite(minutes)
      ) {
        return null;
      }

      const parsedHoursPerDay = Number(scheduleMeta?.hoursPerDay);
      const hoursPerDay =
        Number.isFinite(parsedHoursPerDay) && parsedHoursPerDay > 0
          ? parsedHoursPerDay
          : null;
      if (!hoursPerDay) return null;

      const durationHours =
        durationUnit === "MINUTES"
          ? durationValue / 60
          : durationUnit === "HOURS"
            ? durationValue
            : durationValue * hoursPerDay;
      const totalMinutes = Math.round(durationHours * 60);
      const endOffset = hours * 60 + minutes + totalMinutes;
      const dayOffset = Math.floor(endOffset / (24 * 60));
      const endMinutesInDay = endOffset % (24 * 60);
      const endHours = Math.floor(endMinutesInDay / 60);
      const endMinutes = endMinutesInDay % 60;

      const startDateObj = new Date(year, month - 1, day, hours, minutes, 0, 0);
      const endDateObj = new Date(
        year,
        month - 1,
        day,
        endHours,
        endMinutes,
        0,
        0,
      );
      endDateObj.setDate(endDateObj.getDate() + dayOffset);
      const endDateString = endDateObj.toLocaleDateString("en-CA");
      const endTimeStr = `${String(endHours).padStart(2, "0")}:${String(
        endMinutes,
      ).padStart(2, "0")}`;

      const collapsedBreakdown =
        dayOffset === 0
          ? [
              {
                date: startDateValue,
                dayName: startDateObj.toLocaleDateString("en-IN", {
                  weekday: "short",
                }),
                type: "WORKING",
                isStartDate: true,
                isEndDate: true,
                isPartialDay: true,
                hoursUsed: durationHours,
                label: `${durationHours}h of ${hoursPerDay}h`,
              },
            ]
          : [
              {
                type: "WORKING_RANGE",
                startDate: startDateValue,
                endDate: endDateString,
                label: `Overtime, ends at ${endTimeStr}`,
              },
            ];

      return {
        endDate: endDateString,
        expectedEndDate: endDateObj.toISOString(),
        isOvertime: true,
        overtimeEndTime: endTimeStr,
        workStartTime: scheduleMeta?.workStartTime || startTimeValue,
        workEndTime: endTimeStr,
        hoursPerDay,
        workingDays: Array.isArray(scheduleMeta?.workingDays)
          ? scheduleMeta.workingDays
          : [],
        summary: {
          durationHours,
          workingDaysCount: dayOffset + 1,
          weeklyOffs: 0,
          holidays: 0,
          totalCalendarDays: dayOffset + 1,
          hoursPerDay,
          startedPartialDay: true,
          hoursRemainingOnStartDay: Math.max(
            hoursPerDay - (hours + minutes / 60),
            0,
          ),
        },
        collapsedBreakdown,
      };
    },
    [durationUnit, durationValue],
  );

  const handleOpenReschedule = (assignment) => {
    setReschedulingId(assignment.id);
    const currentStartDate = assignment.startDate
      ? new Date(assignment.startDate).toLocaleDateString("en-CA")
      : todayString();
    const currentStartTime = assignment.scheduledStartTime
      ? toLocalTimeString(assignment.scheduledStartTime)
      : "09:00";

    setRescheduleDate(currentStartDate);
    setRescheduleTime(currentStartTime);
    setRescheduleIsOvertime(!!assignment.isOvertime);
    setRescheduleEndResult(null);
  };

  const handleCloseReschedule = () => {
    setReschedulingId(null);
    setRescheduleDate("");
    setRescheduleTime("");
    setRescheduleIsOvertime(false);
    setRescheduleEndResult(null);
    setRescheduleCalculating(false);
    setRescheduleSaving(false);
  };

  useEffect(() => {
    if (!reschedulingId || !rescheduleDate || !rescheduleTime) {
      setRescheduleEndResult(null);
      return;
    }

    const assignment = activeAssignments.find(
      (item) => item.id === reschedulingId,
    );
    if (!assignment?.assignee?.id) return;

    let active = true;
    const timer = setTimeout(async () => {
      setRescheduleCalculating(true);
      try {
        const res = await scheduleAPI.calculateEndDate(assignment.assignee.id, {
          startDate: rescheduleDate,
          scheduledStartTime: rescheduleTime,
          durationValue,
          durationUnit,
        });

        if (!active) return;

        if (rescheduleIsOvertime) {
          setRescheduleEndResult(
            buildOvertimePreviewResult(
              rescheduleDate,
              rescheduleTime,
              res.data,
            ),
          );
        } else {
          setRescheduleEndResult(res.data);
        }
      } catch {
        if (active) setRescheduleEndResult(null);
      } finally {
        if (active) setRescheduleCalculating(false);
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    activeAssignments,
    buildOvertimePreviewResult,
    durationUnit,
    durationValue,
    rescheduleDate,
    rescheduleIsOvertime,
    rescheduleTime,
    reschedulingId,
  ]);

  const handleAssign = async () => {
    if (!stageUserId || !startDate) return;
    if (!scheduledStartTime) return;
    if (overflowOption === "NEXT_DAY" && !endDateResult?.breakdown) return;

    await onAssignStage({
      stageId: selectedStageId,
      assigneeId: stageUserId,
      startDate: previewParams.startDate, // ← use preview params
      scheduledStartTime: previewParams.scheduledStartTime,
      isOvertime: previewParams.isOvertime,
      existingAssignmentId: maxConcurrent === 1 ? existingAssignment?.id : null,
      instruction: primaryInstruction.trim(),
    });

    setPrimaryInstruction("");
    setStageUserId("");
    setEndDateResult(null);
    setScheduledStartTime("09:00");
    setOverflowOption(null);
    setOverflowData(null);
  };

  const handleReassign = async (assignmentId) => {
    const nextUserId = reassignTargets[assignmentId];
    if (!nextUserId) return;
    await onReassign(assignmentId, { assigneeId: nextUserId });
    setReassignTargets((prev) => ({ ...prev, [assignmentId]: "" }));
  };

  const handleRemove = async (assignmentId) => {
    if (!onRemove) return;
    await onRemove(assignmentId);
  };

  const handleConfirmReschedule = async (assignmentId) => {
    if (!project?.id || !assignmentId || !rescheduleDate || !rescheduleTime)
      return;
    setRescheduleSaving(true);
    try {
      await stageAssignmentsAPI.reschedule(project.id, assignmentId, {
        startDate: rescheduleDate,
        scheduledStartTime: rescheduleTime,
        isOvertime: rescheduleIsOvertime,
      });
      handleCloseReschedule();
      if (onReschedule) await onReschedule();
    } catch (err) {
      console.error("Reschedule failed:", err);
    } finally {
      setRescheduleSaving(false);
    }
  };

  return (
    <div className="stage-assign-tab">
      {activeAssignments.length > 0 && (
        <div className="assign-section">
          <h4 className="assign-section-title">Current Assignees</h4>
          {activeAssignments.map((assignment) => {
            const statusInfo = formatAssignmentStatus(assignment.status);
            const canReassign = assignment.status !== "SUBMITTED";
            const canReschedule = ["PENDING", "IN_PROGRESS"].includes(
              assignment.status,
            );
            const canRemove =
              activeAssignments.length > 1 &&
              ["PENDING", "IN_PROGRESS"].includes(assignment.status);
            const isRescheduling = reschedulingId === assignment.id;
            return (
              <div key={assignment.id} className="assignee-card">
                <div className="assignee-card-info">
                  <div className="assignee-avatar">
                    {assignment.assignee?.firstName?.[0]}
                    {assignment.assignee?.lastName?.[0]}
                  </div>
                  <div>
                    <span className="assignee-name">
                      {assignment.assignee?.firstName}{" "}
                      {assignment.assignee?.lastName}
                    </span>
                    <span
                      className="assignee-status"
                      style={{ color: statusInfo.textColor }}
                    >
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                    {assignment.scheduledStartTime && (
                      <span className="assignee-schedule small-muted">
                        {toLocalTimeDisplay(assignment.scheduledStartTime)}
                        {assignment.expectedEndDate && (
                          <>
                            {" "}
                            {" -> "}{" "}
                            {toLocalTimeDisplay(assignment.expectedEndDate)}
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {canReschedule && !isRescheduling && (
                  <button
                    className="btn-secondary btn-sm assignee-reschedule-btn"
                    onClick={() => handleOpenReschedule(assignment)}
                    disabled={loading}
                  >
                    Reschedule
                  </button>
                )}

                {isRescheduling && (
                  <div className="reschedule-form">
                    <div className="reschedule-form-header">
                      <span className="reschedule-form-title">Reschedule</span>
                      <button
                        className="reschedule-close"
                        onClick={handleCloseReschedule}
                        disabled={rescheduleSaving}
                      >
                        X
                      </button>
                    </div>

                    <div className="reschedule-fields">
                      <div>
                        <label className="field-label">Start Date</label>
                        <input
                          type="date"
                          value={rescheduleDate}
                          min={todayString()}
                          onChange={(e) => setRescheduleDate(e.target.value)}
                          className="date-input"
                        />
                      </div>

                      <div>
                        <label className="field-label">Start Time</label>
                        <input
                          type="time"
                          value={rescheduleTime}
                          onChange={(e) => setRescheduleTime(e.target.value)}
                          className="time-input"
                          step="1800"
                        />
                      </div>
                    </div>

                    <label className="reschedule-overtime-toggle">
                      <input
                        type="checkbox"
                        checked={rescheduleIsOvertime}
                        onChange={(e) =>
                          setRescheduleIsOvertime(e.target.checked)
                        }
                      />
                      <span>Allow overtime</span>
                    </label>

                    {rescheduleCalculating && (
                      <p className="calculating-text">
                        Calculating schedule...
                      </p>
                    )}

                    {rescheduleEndResult && !rescheduleCalculating && (
                      <EndDateBreakdown
                        result={rescheduleEndResult}
                        userName={assignment.assignee?.firstName || "User"}
                        workingDays={rescheduleEndResult.workingDays || []}
                      />
                    )}

                    <div className="reschedule-actions">
                      <button
                        className="btn-primary"
                        disabled={
                          rescheduleSaving || !rescheduleDate || !rescheduleTime
                        }
                        onClick={() => handleConfirmReschedule(assignment.id)}
                      >
                        {rescheduleSaving ? "Saving..." : "Confirm Reschedule"}
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={handleCloseReschedule}
                        disabled={rescheduleSaving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {canReassign && !isRescheduling && (
                  <div className="reassign-row">
                    <select
                      value={reassignTargets[assignment.id] || ""}
                      onChange={(e) =>
                        setReassignTargets((prev) => ({
                          ...prev,
                          [assignment.id]: e.target.value,
                        }))
                      }
                      className="reassign-select"
                      disabled={loading}
                    >
                      <option value="">Reassign to...</option>
                      {stageEligibleUsers
                        .filter((user) => user.id !== assignment.assignee?.id)
                        .map((user) => {
                          const isAlreadyAssigned = assignedUserIds.has(
                            user.id,
                          );
                          return (
                            <option key={user.id} value={user.id}>
                              {user.firstName} {user.lastName}
                              {user.roles?.[0]?.name
                                ? ` (${user.roles[0].name})`
                                : ""}
                              {isAlreadyAssigned ? " - already assigned" : ""}
                            </option>
                          );
                        })}
                    </select>
                    <button
                      className="btn-secondary"
                      disabled={loading || !reassignTargets[assignment.id]}
                      onClick={() => handleReassign(assignment.id)}
                    >
                      Reassign
                    </button>
                    {canRemove && (
                      <button
                        className="btn-danger btn-sm"
                        disabled={loading}
                        onClick={() => handleRemove(assignment.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(canAssignMoreLocal || maxConcurrent > 1) && (
        <div className="assign-section">
          <h4 className="assign-section-title">
            {activeAssignments.length > 0
              ? "Add Another Assignee"
              : "Assign Stage"}
          </h4>

          <EligibleUserSelect
            projectId={project.id}
            stageId={selectedStageId}
            value={stageUserId}
            onChange={setStageUserId}
            disabled={loading}
          />

          {stageUserId && (
            <>
              {userScheduleSummary && (
                <div className="schedule-summary">
                  {activeShiftName && (
                    <span className="schedule-summary-shift">
                      {activeShiftName}
                    </span>
                  )}
                  <span>
                    {(userScheduleSummary.workingDays || [])
                      .map((day) => day[0] + day.slice(1, 3).toLowerCase())
                      .join(" ")}
                  </span>
                  <span>
                    {userScheduleSummary.workStartTime} —{" "}
                    {userScheduleSummary.workEndTime}
                  </span>
                </div>
              )}

              <div className="start-date-field">
                <label className="field-label">Start Date</label>
                <p className="field-helper">
                  Task appears on their schedule from this date
                </p>
                <input
                  type="date"
                  value={startDate}
                  min={todayString()}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`date-input ${
                    showConflictWarning ? "date-input--conflict" : ""
                  } ${isStartDateWO ? "date-input--wo" : ""}`}
                />
                {isStartDateWO && (
                  <p className="wo-date-warning">
                    Weekly off for {activeShiftName || "this user"} — task will
                    be scheduled on the next working day
                  </p>
                )}
              </div>

              <div className="start-time-field">
                <label className="field-label">Start Time</label>
                {startTimeSuggestion && (
                  <p className="field-helper">
                    Suggested: {startTimeSuggestion.suggestedStartTime}
                    {startTimeSuggestion.existingTasksOnDay.length > 0
                      ? ` — after ${startTimeSuggestion.existingTasksOnDay.length} existing task${
                          startTimeSuggestion.existingTasksOnDay.length !== 1
                            ? "s"
                            : ""
                        }`
                      : " — start of working day"}
                  </p>
                )}
                <input
                  type="time"
                  value={scheduledStartTime}
                  onChange={(e) => setScheduledStartTime(e.target.value)}
                  className="time-input"
                  step="1800"
                />

                {startTimeSuggestion?.existingTasksOnDay?.length > 0 && (
                  <div className="existing-tasks-on-day">
                    <p className="existing-tasks-label">Already scheduled:</p>
                    {startTimeSuggestion.existingTasksOnDay.map((task) => (
                      <div key={task.id} className="existing-task-row">
                        <span className="existing-task-time">
                          {task.startTime} - {task.endTime}
                        </span>
                        <span className="existing-task-name">
                          {task.stageName}
                        </span>
                        <span className="existing-task-project">
                          {task.projectName}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* {startTimeSuggestion?.predecessorConstraint?.type ===
                  "TOO_EARLY" && (
                  <div className="predecessor-warning">
                    <span className="predecessor-warning-icon">?</span>
                    <div>
                      <p>
                        <strong>
                          {startTimeSuggestion.predecessorConstraint.stageName}
                        </strong>{" "}
                        (assigned to{" "}
                        {startTimeSuggestion.predecessorConstraint.assigneeName}
                        ) is not expected to finish until{" "}
                        <strong>
                          {startTimeSuggestion.predecessorConstraint.endsDate}
                        </strong>
                        .
                      </p>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => {
                          setStartDate(
                            startTimeSuggestion.predecessorConstraint
                              .recommendedDate,
                          );
                          setScheduledStartTime(
                            startTimeSuggestion.predecessorConstraint
                              .recommendedTime,
                          );
                        }}
                      >
                        Move to{" "}
                        {
                          startTimeSuggestion.predecessorConstraint
                            .recommendedDate
                        }
                      </button>
                    </div>
                  </div>
                )} */}

                {startTimeSuggestion?.predecessorConstraint?.type ===
                  "SAME_DAY" && (
                  <p className="field-helper predecessor-helper">
                    Predecessor (
                    {startTimeSuggestion.predecessorConstraint.stageName}) ends
                    at {startTimeSuggestion.predecessorConstraint.endsAt} so
                    start time is adjusted
                  </p>
                )}
              </div>

              {overflowData && (
                <div className="overflow-warning">
                  <div className="overflow-warning-header">
                    <span className="overflow-icon">⚠</span>
                    <div>
                      <p className="overflow-title">
                        Task extends past working hours
                      </p>
                      <p className="overflow-subtitle">
                        Ends at {overflowData.endTime} —{" "}
                        {Math.round((overflowData.overtimeMinutes / 60) * 10) /
                          10}
                        h past {overflowData.workEndTime}
                      </p>
                    </div>
                  </div>

                  <p className="overflow-question">
                    How would you like to handle this?
                  </p>
                  <div className="overflow-options">
                    <label
                      className={`overflow-option ${overflowOption === "OVERTIME" ? "selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="overflow"
                        value="OVERTIME"
                        checked={overflowOption === "OVERTIME"}
                        onChange={() => setOverflowOption("OVERTIME")}
                      />
                      <div>
                        <span className="overflow-option-title">
                          Allow overtime
                        </span>
                        <span className="overflow-option-desc">
                          {scheduledStartTime} → {overflowData.endTime} — works
                          past end time
                        </span>
                      </div>
                    </label>

                    <label
                      className={`overflow-option ${overflowOption === "SPLIT" ? "selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="overflow"
                        value="SPLIT"
                        checked={overflowOption === "SPLIT"}
                        onChange={() => setOverflowOption("SPLIT")}
                      />
                      <div>
                        <span className="overflow-option-title">
                          Split across days
                        </span>
                        <span className="overflow-option-desc">
                          Starts {scheduledStartTime}, continues next working
                          day
                          {endDateResult?.endDate && overflowOption === "SPLIT"
                            ? ` — ends ${endDateResult.endDate}`
                            : ""}
                        </span>
                      </div>
                    </label>

                    <label
                      className={`overflow-option ${overflowOption === "NEXT_DAY" ? "selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="overflow"
                        value="NEXT_DAY"
                        checked={overflowOption === "NEXT_DAY"}
                        onChange={() => setOverflowOption("NEXT_DAY")}
                      />
                      <div>
                        <span className="overflow-option-title">
                          Move to next day
                        </span>
                        <span className="overflow-option-desc">
                          Starts {startTimeSuggestion?.workStartTime || "09:00"}{" "}
                          next working day
                          {endDateResult?.endDate &&
                          overflowOption === "NEXT_DAY"
                            ? ` — ends ${endDateResult.endDate}`
                            : ""}
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {showConflictWarning && (
                <ConflictWarning
                  conflicts={conflictData.conflicts}
                  chosenStartDate={startDate}
                  onUseLatestDate={(dateStr) => {
                    setStartDate(dateStr);
                    setConflictDismissed(false);
                  }}
                  onKeepDate={() => setConflictDismissed(true)}
                />
              )}

              {conflictData?.hasConflict && conflictDismissed && (
                <div className="conflict-dismissed">
                  <span>⚠ Start date may conflict with predecessor</span>
                  <button
                    className="conflict-dismissed-undo"
                    onClick={() => setConflictDismissed(false)}
                  >
                    Review
                  </button>
                </div>
              )}

              {isCalculating && (
                <p className="calculating-text">Calculating schedule...</p>
              )}
              {endDateResult && !isCalculating && (
                <EndDateBreakdown
                  result={endDateResult}
                  userName={selectedUser?.firstName || "User"}
                  workingDays={endDateResult.workingDays || []}
                />
              )}

              {/* {queuePosition && (
                <p className="queue-position-note">
                  Will be position {queuePosition} in queue
                </p>
              )} */}
            </>
          )}

          {stageUserId && (
            <div className="primary-instruction-field">
              <label className="field-label">
                Work Instruction <span className="small-muted">(optional)</span>
              </label>
              <p className="field-helper">
                Sent with the allocation and shown at the top of their task
                page. Appears in the Work Instructions tab.
              </p>
              <textarea
                className="instruction-input"
                rows={2}
                value={primaryInstruction}
                onChange={(e) => setPrimaryInstruction(e.target.value)}
                placeholder={`e.g. ${
                  selectedUser?.firstName || "You"
                } — take Task C and Task D`}
                disabled={loading}
              />
            </div>
          )}

          <button
            className={`btn-primary assign-submit-btn ${
              showConflictWarning ? "btn-warning" : ""
            }`}
            disabled={
              loading ||
              !stageUserId ||
              selectedAlreadyAssigned ||
              !canAssignMoreLocal ||
              (overflowOption === "NEXT_DAY" && !endDateResult?.breakdown)
            }
            onClick={handleAssign}
          >
            {showConflictWarning
              ? "⚠ Assign Anyway"
              : maxConcurrent === 1 && existingAssignment
                ? "Reassign"
                : "Assign"}
          </button>

          {selectedAlreadyAssigned && (
            <p className="small-muted">
              This user is already assigned to this stage
            </p>
          )}
        </div>
      )}

      {!canAssignMoreLocal && maxConcurrent > 1 && (
        <p className="small-muted">
          Max concurrent assignees ({maxConcurrent}) reached
        </p>
      )}
    </div>
  );
}
